import { APP_INITIALIZER, Inject, Injectable } from '@angular/core'
import { AuthenticationResult, Configuration as msalConfig, InteractionRequiredAuthError, LogLevel, PublicClientApplication } from '@azure/msal-browser'
import { BehaviorSubject, Observable, catchError, distinctUntilChanged, filter, from, interval, map, of, startWith, switchMap, tap, throwError } from 'rxjs'
import { APP_CONFIG } from '../configurations/configurations.factory'
import { AuthenticationConfigurations } from '../configurations/models/authentication-configurations.model'
import { Configurations } from '../configurations/models/configurations.model'
import { Router } from '@angular/router'

@Injectable({
  providedIn: 'root',
  deps: [APP_INITIALIZER]
})
export class AuthenticationService {
  private readonly clientApplication: PublicClientApplication
  private readonly authConfig: AuthenticationConfigurations
  private accountsSubject = new BehaviorSubject<any[]>([])

  constructor(@Inject(APP_CONFIG) config: Configurations, private router: Router) {
    this.authConfig = config.authenticationConfig

    const msalConfig: msalConfig = {
      auth: {
        clientId: this.authConfig.clientId,
        authority: `${this.authConfig.authority}/${this.authConfig.signInPolicy}`,
        knownAuthorities: this.authConfig.knownAuthorities,
        redirectUri: this.authConfig.redirectUri ?? location.origin,
      },
      cache: {
        cacheLocation: this.authConfig.cacheLocation,
        storeAuthStateInCookie: this.isIE()
      },
      system: {
        loggerOptions: {
          loggerCallback(logLevel: LogLevel, message: string) {
            console.log(message)
          },
          logLevel: LogLevel.Error,
          piiLoggingEnabled: false
        }
      }
    }

    this.clientApplication = new PublicClientApplication(msalConfig)
    this.handleRedirectCallback()
    this.startAccountMonitoring()
  }

  private startAccountMonitoring() {
    interval(3000).pipe(
      map(() => this.clientApplication.getAllAccounts()),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      tap(accounts => {
        this.accountsSubject.next(accounts)
      })
    ).subscribe()
  }

  getAccountsObservable(): Observable<any[]> {
    return this.accountsSubject.asObservable()
  }

  loginWithRedirect(state: { target: string }) {
    this.clearInteractionToken() // Fix a msal bug
    return from(
      this.clientApplication.loginRedirect({
        scopes: this.authConfig.scopes,
        state: JSON.stringify({ ...state, passwordReset: false }),
      })
    )
  }

  loginWithPopup() {
    this.clearInteractionToken() // Fix a msal bug
    return from(
      this.clientApplication.loginPopup({
        scopes: this.authConfig.scopes,
      })
    )
  }

  loginWithSSO(loginHint?: string) {
    const urlParams = new URLSearchParams(window.location.search)
    const sid = urlParams.get("sid")
    console.log('loginhint', sid, this.authConfig.scopes, loginHint)
    return from(
      this.clientApplication.ssoSilent({
        sid: sid!,
        scopes: this.authConfig.scopes,
        loginHint: loginHint,
      })
    ).pipe(
      tap((result) => console.log('result', result)),
      map(result => ({ sub: result.uniqueId, ...result.idTokenClaims })),
      catchError(error => {
        if (error instanceof InteractionRequiredAuthError) {
          return of(null)
        } else {
          return throwError(error)
        }
      })
    )
  }

  logoutWithPopUp() {
    return from(this.clientApplication.logoutPopup({ postLogoutRedirectUri: location.origin }))
  }

  logoutWithRedirect() {
    return from(this.clientApplication.logoutRedirect({ postLogoutRedirectUri: location.origin }))
  }

  handleRedirectCallback() {
    from(this.clientApplication.handleRedirectPromise()).pipe(
      filter((result): result is AuthenticationResult => result !== null),
      map(result => {
        const state = result.state ? JSON.parse(result.state) : undefined
        const target = state?.target || '/'
        this.router.navigateByUrl(target)
        return result
      }),
      catchError(error => {
        console.error(error)
        return of(undefined)
      })
    ).subscribe()
  }

  getAccessToken() {
    const account = this.clientApplication.getAllAccounts()[0]
    const scopes = this.authConfig.scopes

    return from(this.clientApplication.acquireTokenSilent({ account, scopes })).pipe(map(result => result.accessToken))
  }

  checkSession(): Observable<any> {
    return this.getAccountsObservable().pipe(
      switchMap(accounts => {
        if (accounts.length === 0) {
          return from(
            this.clientApplication.ssoSilent({
              scopes: this.authConfig.scopes
            })
          ).pipe(
            map(result => ({ sub: result.uniqueId, ...result.idTokenClaims })),
            catchError(error => {
              if (error instanceof InteractionRequiredAuthError) {
                return of(null)
              } else if (error.message.includes("NoExtension")) {
                console.error("SSO extension is not installed.")
                return of(null)
              } else {
                return throwError(() => error)
              }
            })
          )
        }

        return from(
          this.clientApplication.acquireTokenSilent({
            account: accounts[0],
            scopes: this.authConfig.scopes,
          })
        ).pipe(
          map(result => ({ sub: result.uniqueId, ...result.idTokenClaims })),
          catchError(error => {
            if (error instanceof InteractionRequiredAuthError) {
              return of(null);
            } else {
              return throwError(() => error)
            }
          })
        )
      })
    )
  }

  clearInteractionToken() {
    sessionStorage.removeItem('msal.interaction.status')
  }

  private isIE() {
    return window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1
  }
}
